import * as React from 'react';
import { Box, StyledInput, NoDataHandler, Button } from '../../components';
import { Query, Mutation } from 'react-apollo';
import { Span, H4 } from '../../typography';
import FillLoader from '../loaders/FillLoader';
import { DocumentNode } from 'graphql';
import { debounce } from 'lodash';
import styled from 'styled-components';
import ReactTable, { RowInfo, ComponentDecoratorProps } from 'react-table';
import { formatGqlError } from '../../utils/helpers';
import { TableProps } from '../Table';

type Props<D = any, ResolvedData = D> = {
  searchValue?: string;
  query: DocumentNode;
  columns?: Array<{}>;
  placeholder: string;
  getTrProps?: ComponentDecoratorProps;
  onRowClick: (rowInfo: any) => void;
  onSuccess: () => void;
  stakeholderID?: string;
  propertyID?: string;
  searchVariables: object;
  refetchVariable: object;
  refetchQuery: DocumentNode;
  searchMutation: DocumentNode;
  searchType: string;
  dataAccessor: string;
  secondDataAccessor?: string;
  queryTerm: string;
  resolveData?: (data: D[]) => ResolvedData[];
};

type State = {
  text?: string;
  selectedResult: any;
  isResultSelected: boolean;
};

const LoaderWrap = styled.div`
  padding-top: 120px;
`;

export const StyledSearchTable = styled(ReactTable)<TableProps>`
  flex: 1;
  width: 100%;
  background: white;
  max-height: 300px;
  border-radius: 4px;
  & .rt-thead.-header {
    box-shadow: 0 0px 4px 0px rgba(50, 50, 93, 0.3) !important;
  }
  & .rt-thead .rt-th {
    padding: 5px !important;
  }
  & .rt-td {
    justify-content: center;
    align-items: center;
    display: flex;
    white-space: normal !important;
    text-align: center !important;
  }
  & .rt-tr-group:hover {
    cursor: pointer;
  }
`;

class StyledSearch extends React.Component<Props, State> {
  static defaultProps = {
    data: [],
    columns: [],
    defaultPageSize: 10,
  };

  // prettier-ignore
  debounceSetState = debounce((text: string) => {
    this.setState({ text });
  },                          1000);

  constructor(props: Props) {
    super(props);
    this.state = {
      text: '',
      selectedResult: null,
      isResultSelected: false,
    };
  }

  handleChange = (e: React.SyntheticEvent): void => {
    let target = e.target as HTMLInputElement;
    const input = target.value;
    this.debounceSetState(input);
  };

  handleRowClick = (rowInfo: RowInfo) => {
    const { onRowClick } = this.props;
    if (onRowClick) {
      onRowClick(rowInfo.original.id);
      this.setState(
        {
          selectedResult: rowInfo.original,
        },
        () => {
          this.setState({
            isResultSelected: true,
          });
        }
      );
    }
    return null;
  };

  handleSuccess = (data: any) => {
    // TODO: Add toast on success
    this.props.onSuccess();
  };

  render() {
    const { text, selectedResult, isResultSelected } = this.state;
    const {
      query,
      placeholder,
      columns,
      searchVariables,
      refetchVariable,
      refetchQuery,
      searchMutation,
      searchType,
      dataAccessor,
      secondDataAccessor,
      resolveData,
      queryTerm: QUERY_TERM,
    } = this.props;
    return (
      <Box {...this.props} width="100%">
        {!isResultSelected ? <StyledInput placeholder={placeholder} onChange={this.handleChange} mb={4} /> : null}
        <Query query={query} variables={{ text }}>
          {({ data, loading, error }) => {
            if (loading) {
              return (
                <LoaderWrap>
                  <FillLoader width={30} height={30} />
                </LoaderWrap>
              );
            }
            if (error) {
              return (
                <Span width="100%" textAlign="center" color="error" mb={3}>
                  {formatGqlError(error)}
                </Span>
              );
            }
            if (data && data[QUERY_TERM].length > 0) {
              return isResultSelected ? (
                <Mutation
                  onCompleted={(result: any) => this.handleSuccess(result)}
                  mutation={searchMutation}
                  awaitRefetchQueries={true}
                  refetchQueries={[{ query: refetchQuery, variables: refetchVariable }]}
                  variables={searchVariables}
                >
                  {(mutation, { loading: isLoading }) => {
                    if (isLoading) {
                      return (
                        <LoaderWrap>
                          <FillLoader width={30} height={30} />
                        </LoaderWrap>
                      );
                    }
                    return (
                      <Box width="100%" flex={1} alignItems="center">
                        <H4 fontWeight="lighter" mb={4}>
                          {selectedResult[dataAccessor]} {/* prettier-ignore */}
                          {secondDataAccessor ? selectedResult[secondDataAccessor] : ''}{' '}
                        </H4>
                        <Span>Are you sure you want to link this {searchType}?</Span>
                        <Box width="100%" flexDirection="row" mt={4}>
                          <Button
                            onClick={() => {
                              this.setState({
                                isResultSelected: false,
                              });
                            }}
                            type="button"
                            text="Go Back"
                            bg="coal"
                            mr={2}
                          />
                          <Button onClick={mutation} type="button" text="Confirm" bg="putty" />
                        </Box>
                      </Box>
                    );
                  }}
                </Mutation>
              ) : (
                <Box width="100%" flex={1}>
                  <StyledSearchTable
                    data={data[QUERY_TERM]}
                    columns={columns}
                    className="-striped -highlight"
                    defaultPageSize={5}
                    showPageSizeOptions={false}
                    resolveData={resolveData}
                    getTrProps={(trprops: ComponentDecoratorProps, rowInfo: RowInfo) => {
                      if (rowInfo) {
                        return {
                          onClick: () => {
                            this.handleRowClick(rowInfo);
                          },
                        };
                      }
                      return {};
                    }}
                  />
                </Box>
              );
            }
            return <NoDataHandler />;
          }}
        </Query>
      </Box>
    );
  }
}

export default StyledSearch;



// WEBPACK FOOTER //
// ./src/components/formElements/StyledSearch.tsx